import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_ITINERARY_PAGE_CONTENT_LOADED,
  EVENT_LIST_PAGE_CONTENT_LOADED,
  EVENT_LOGGED_OUT_POST_VIEW_CONTENT_LOADED,
  EVENT_LOGGED_OUT_POST_VIEW_PAGE_VIEWED,
  EVENT_PLACE_PAGE_CONTENT_LOADED,
} from '@/lib/analyticsEvents';
import type { PlatformBrowseMode } from '@/constants/platformBrowseMode';
import type { ShareLinkType } from '@/lib/analyticsEvents';

export type ContentPageKind = 'list' | 'place' | 'itinerary';

export type ViewerUserState = 'logged_in' | 'logged_out';

export type { ShareLinkType };

/** Common viewer props for events fired by both guests and logged-in users. */
export function getViewerAnalyticsProps(options: {
  hasUser: boolean;
  browseMode?: PlatformBrowseMode | null;
}): { user_state: ViewerUserState; browse_mode?: PlatformBrowseMode } {
  return {
    user_state: options.hasUser ? 'logged_in' : 'logged_out',
    ...(options.browseMode ? { browse_mode: options.browseMode } : {}),
  };
}

function contentLoadedEventName(kind: ContentPageKind): string {
  if (kind === 'list') return EVENT_LIST_PAGE_CONTENT_LOADED;
  if (kind === 'place') return EVENT_PLACE_PAGE_CONTENT_LOADED;
  return EVENT_ITINERARY_PAGE_CONTENT_LOADED;
}

function pageNameForKind(kind: ContentPageKind): string {
  if (kind === 'list') return 'List';
  if (kind === 'place') return 'Place';
  return 'Itinerary';
}

/**
 * Fires shared content-loaded analytics for list/place/itinerary routes.
 * For logged-out viewers, also fires Logged Out Post View page/content events.
 */
export function trackContentPageLoaded(options: {
  kind: ContentPageKind;
  postId: string;
  hasUser: boolean;
  hasShareToken: boolean;
  browseMode?: PlatformBrowseMode | null;
  loadTimeMs: number;
  fromCache?: boolean;
}): void {
  const {
    kind,
    postId,
    hasUser,
    hasShareToken,
    browseMode,
    loadTimeMs,
    fromCache = false,
  } = options;

  const viewer = getViewerAnalyticsProps({ hasUser, browseMode });
  const pageName = pageNameForKind(kind);
  const sharedProps = {
    ...viewer,
    page_name: pageName,
    post_id: postId,
    post_type: kind,
    has_share_token: hasShareToken,
    load_time_ms: loadTimeMs,
    from_cache: fromCache,
  };

  WebAnalytics.track(contentLoadedEventName(kind), sharedProps);

  if (!hasUser) {
    WebAnalytics.trackLoggedOutPageView(EVENT_LOGGED_OUT_POST_VIEW_PAGE_VIEWED, pageName, {
      post_id: postId,
      post_type: kind,
      has_share_token: hasShareToken,
      ...(browseMode ? { browse_mode: browseMode } : {}),
    });
    WebAnalytics.trackLoggedOutPageView(EVENT_LOGGED_OUT_POST_VIEW_CONTENT_LOADED, pageName, {
      post_id: postId,
      post_type: kind,
      has_share_token: hasShareToken,
      load_time_ms: loadTimeMs,
      from_cache: fromCache,
      ...(browseMode ? { browse_mode: browseMode } : {}),
    });
  }
}
